import { clampPageNumber } from './listPagination';

const normalizeStringArray = (value) =>
  Array.isArray(value) ? value.map((item) => String(item ?? '')).filter(Boolean) : [];

export function buildUserBuildingSearchFilterOptions(users) {
  const numOfBuildings = [];
  const listOfJobTitle = [];
  const buildingValues = new Set();
  const jobTitleValues = new Set();

  (Array.isArray(users) ? users : []).forEach((user) => {
    const propertyCount = user?.property?.length ?? 0;
    if (!buildingValues.has(propertyCount)) {
      buildingValues.add(propertyCount);
      numOfBuildings.push({ text: String(propertyCount), value: propertyCount });
    }

    const jobTitle = user?.accepter?.job_title;
    if (jobTitle && !jobTitleValues.has(jobTitle)) {
      jobTitleValues.add(jobTitle);
      listOfJobTitle.push({ text: jobTitle, value: jobTitle });
    }
  });

  numOfBuildings.sort((a, b) => Number(a.value) - Number(b.value));
  listOfJobTitle.sort((a, b) => String(a.text).localeCompare(String(b.text)));

  return { numOfBuildings, listOfJobTitle };
}

function getUserSortValue(user, field) {
  switch (field) {
    case 'accepter.username':
      return user?.accepter?.username ?? '';
    case 'accepter.job_title':
      return user?.accepter?.job_title ?? '';
    case 'accepter_email':
      return user?.accepter_email ?? '';
    case 'property.length':
      return user?.property?.length ?? 0;
    case 'accepter_role':
      return user?.accepter_role ?? '';
    case 'createdAt':
      return user?.createdAt ?? '';
    case 'accepter.phone':
      return user?.accepter?.phone ?? '';
    default:
      return '';
  }
}

export function filterUserBuildingSearchUsers(
  users,
  { searchText = '', selectedBuilings = [], selectedJobTitle = [], selectedInvitedDate = '' } = {},
) {
  const rows = Array.isArray(users) ? users : [];
  const query = String(searchText || '')
    .trim()
    .toLowerCase();
  const propertyCounts = normalizeStringArray(selectedBuilings);
  const jobTitles = normalizeStringArray(selectedJobTitle);
  const invitedDate =
    selectedInvitedDate != null && String(selectedInvitedDate).trim() !== ''
      ? new Date(selectedInvitedDate).toISOString().slice(0, 10)
      : '';

  return rows.filter((user) => {
    if (query) {
      const haystack = [
        user?.accepter?.username,
        user?.accepter_email,
        user?.accepter?.job_title,
        user?.accepter?.phone,
        user?.accepter_role,
      ]
        .map((value) => String(value || '').toLowerCase())
        .join(' ');
      if (!haystack.includes(query)) {
        return false;
      }
    }

    if (
      propertyCounts.length > 0 &&
      !propertyCounts.includes(String(user?.property?.length ?? 0))
    ) {
      return false;
    }

    if (jobTitles.length > 0 && !jobTitles.includes(String(user?.accepter?.job_title ?? ''))) {
      return false;
    }

    if (invitedDate) {
      const userInvitedDate = user?.createdAt
        ? new Date(user.createdAt).toISOString().slice(0, 10)
        : '';
      if (userInvitedDate !== invitedDate) {
        return false;
      }
    }

    return true;
  });
}

export function sortUserBuildingSearchUsers(users, sortOptions = {}) {
  const rows = Array.isArray(users) ? [...users] : [];
  const activeSort = Object.entries(sortOptions || {}).find(
    ([, direction]) => direction === 1 || direction === -1,
  );
  if (!activeSort) {
    return rows;
  }

  const [field, direction] = activeSort;
  const multiplier = direction === 1 ? 1 : -1;

  return rows.sort((leftUser, rightUser) => {
    const left = getUserSortValue(leftUser, field);
    const right = getUserSortValue(rightUser, field);

    if (field === 'property.length') {
      return (Number(left) - Number(right)) * multiplier;
    }
    if (field === 'createdAt') {
      return (new Date(left).getTime() - new Date(right).getTime()) * multiplier;
    }
    return String(left).localeCompare(String(right), undefined, { numeric: true }) * multiplier;
  });
}

export function paginateUserBuildingSearchUsers(users, pageNumber, rowsPerPage) {
  const rows = Array.isArray(users) ? users : [];
  const safePage = clampPageNumber(pageNumber, rows.length, rowsPerPage);
  const start = safePage * rowsPerPage;
  return rows.slice(start, start + rowsPerPage);
}
